import _ from "lodash";

import AssetUtils from "../utils/AssetUtils";
import BaseElement from "./BaseElement";
import BlockState from "./BlockState";
import StatesElement from "./StatesElement";
import Block from "./Block";
import Page from "./Page";
import AppConstants from "../constants/AppConstants";
import { TAG_DIV } from "../constants/TextTags";
import Icons from "../constants/Icons";
import {
  FORM_FIELD_DATA_TYPE_TEXT,
  FORM_FIELD_STYLE_BY_TYPE,
  isFormFieldElementType,
  SEEDED_FORM_FIELD_CONTENT,
  SEEDED_FORM_INPUT_CONTENT,
  SUBMIT_BUTTON_TEXT,
  SUBMIT_FORM_LINK,
} from "../constants/FormFieldConstants.mjs";

const SUPPORTED_ASSET_TO_ELEMENT_TYPES = [
  AppConstants.ELEMENT_TYPE_SVG,
  AppConstants.ELEMENT_TYPE_GRAPHIC,
  AppConstants.ELEMENT_TYPE_VIDEO,
  AppConstants.ELEMENT_TYPE_LOTTIE,
];

// THESE WILL MOVE AND EXPAND AS IT GROWS
const BUTTON_PRESETS = {
  type: AppConstants.ELEMENT_TYPE_BUTTON,
  anchor: "center",
  label: "Learn More",
};
const TEXT_STYLE_TYPES = [
  AppConstants.TEXT_STYLE_TITLE,
  AppConstants.TEXT_STYLE_HEADING,
  AppConstants.TEXT_STYLE_SUBHEADING,
  AppConstants.TEXT_STYLE_PARAGRAPH,
];

const ModelFactory = {
  createElementFromAsset: (asset, props) => {
    const elementType = AssetUtils.getElementType(asset);
    let elem = false;

    if (_.includes(SUPPORTED_ASSET_TO_ELEMENT_TYPES, elementType)) {
      elem = _.cloneDeep(BaseElement);
      elem.type = elementType;
      elem.content = AssetUtils.getElementAsset(asset);

      elem = _.merge(elem, props);
    }

    return elem;
  },

  getBlock: () => _.cloneDeep(Block),

  getSiteBlock: () => {
    const block = ModelFactory.getBlock();
    block.pType = AppConstants.SITE_ITEM_SITE;
    block.name = "New Site Canvas";
    block.slug = "new-site-canvas";

    return block;
  },

  getPage: () => _.cloneDeep(Page),

  getBlockState: () => _.cloneDeep(BlockState),

  getStatesElement: () => _.cloneDeep(StatesElement),

  getTextElement: (props) => {
    const elem = _.cloneDeep(BaseElement);
    elem.type = AppConstants.ELEMENT_TYPE_TEXT;
    elem.style = props.style;
    elem.desktop.style = props.style;
    elem.content.text = _.capitalize(props.style);
    elem.content.sample = elem.content.text;

    return elem;
  },

  getHTMLTextElement: () => {
    const elem = ModelFactory.getTextElement({ style: AppConstants.TEXT_STYLE_PARAGRAPH });
    elem.tag = TAG_DIV;
    elem.type = AppConstants.ELEMENT_TYPE_HTML_TEXT;

    return elem;
  },

  getButton: () => {
    const button = _.merge(_.cloneDeep(BaseElement), BUTTON_PRESETS);
    button.style = AppConstants.BUTTON_STYLE_PRIMARY;

    return button;
  },

  /**
   * Builds a form field with only its content and a `style` pointer at its form style, so every
   * visual prop inherits from the theme until overridden.
   * @param {Object} [options]
   * @param {string} [options.type] One of `FORM_FIELD_ELEMENT_TYPES`.
   * @param {string} [options.dataType] Inputs only — one of `FORM_FIELD_DATA_TYPE_*`.
   * @param {boolean} [options.multiLine] Inputs only — renders as a textarea.
   * @returns {Object} The form-field element data.
   */
  getFormFieldElement: ({
    type = AppConstants.ELEMENT_TYPE_FORM_INPUT,
    dataType = FORM_FIELD_DATA_TYPE_TEXT,
    multiLine = false,
  } = {}) => {
    const element = _.cloneDeep(BaseElement);
    element.type = type;
    element.style = FORM_FIELD_STYLE_BY_TYPE[type];
    element.content = _.cloneDeep(SEEDED_FORM_FIELD_CONTENT[type]);

    if (type === AppConstants.ELEMENT_TYPE_FORM_INPUT) {
      Object.assign(element.content, SEEDED_FORM_INPUT_CONTENT[dataType], { dataType });
      // 1 rather than true, as the properties panel writes it.
      if (multiLine) element.content.multiLine = 1;
    }

    return element;
  },

  /**
   * Builds a form's Submit Button: an ordinary button whose link submits the form on the
   * current canvas.
   * @returns {Object} A button element with form-submit link presets.
   */
  getSubmitButton: () => {
    const button = ModelFactory.getButton();
    button.label = SUBMIT_BUTTON_TEXT;
    button.link = { ...SUBMIT_FORM_LINK };

    return button;
  },

  getElement: (type) => {
    if (type === AppConstants.ELEMENT_TYPE_BUTTON) {
      return ModelFactory.getButton();
    }
    if (isFormFieldElementType(type)) {
      return ModelFactory.getFormFieldElement({ type });
    }
    if (TEXT_STYLE_TYPES.includes(type)) {
      return ModelFactory.getTextElement({ style: type });
    }
    const elem = _.cloneDeep(BaseElement);
    elem.type = type;
    if (type === AppConstants.ELEMENT_TYPE_SIMPLE || type === AppConstants.ELEMENT_TYPE_OVAL) {
      elem.desktop.bgcolor = AppConstants.DEFAULT_SIMPLE_COLOR;
      elem.desktop.fillType = AppConstants.BG_TYPE_COLOR;
      if (type === AppConstants.ELEMENT_TYPE_OVAL) {
        elem.type = AppConstants.ELEMENT_TYPE_SIMPLE;
        elem.desktop.border = {
          rad: "50%",
        };
      }
    } else if (type === AppConstants.ELEMENT_TYPE_ICON) {
      elem.desktop.c = AppConstants.DEFAULT_ICON_COLOR;
      elem.content = { icon: Icons.DEFAULT_ICON };
    } else if (type === AppConstants.ELEMENT_TYPE_VIDEO) {
      elem.content = { type: AppConstants.VIDEO_TYPE_VIMEO };
    } else if (type === AppConstants.ELEMENT_TYPE_SOCIAL_GRID) {
      elem.content = { type: AppConstants.DEFAULT_SOCIAL_GRID_PROPERTIES };
    } else if (type === AppConstants.ELEMENT_TYPE_LINE) {
      elem.desktop = { ...elem.desktop, ...AppConstants.DEFAULT_LINE_PROPERTIES };
    } else if (type === AppConstants.ELEMENT_TYPE_LOTTIE) {
      elem.content = {
        type: AppConstants.LOTTIE_TYPE_ASSET,
        loop: 1,
      };
    }
    return elem;
  },
};

export default ModelFactory;
